import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'

import type { GetToken } from '~/config'
import { createClient } from '~/data/graphql'
import { fetchAgentAccount, fetchAgentActivities } from '~/data/interact'
import { parseApiTime } from '~/data/meetings'
import type { Person } from '~/data/types'
import { theme } from '~/theme'

// The agent terminal: a live tail of the agent's inner-monologue stream
// (agentActivities — thoughts, steps, tool calls, errors) with the box's
// presence snapshot as the status line. Owner-gated server-side.

const TYPE_COLOR: Record<string, string> = {
  thought: '#8b93a7',
  step: '#c9d2e0',
  subtask: '#e0a13a',
  tool: '#7ff0ff',
  error: '#ff6b6b',
  note: '#c9c3ff',
  session_start: '#54d18c',
  session_end: '#6a6a72',
}
const STATE_COLOR: Record<string, string> = {
  ONLINE: '#54d18c',
  WORKING: '#7ff0ff',
  IDLE: '#e0a13a',
  BLOCKED: '#ff6b6b',
  OFFLINE: '#6a6a72',
}

function clock(createdAt: string): string {
  const ms = parseApiTime(createdAt)
  if (Number.isNaN(ms)) return '--:--'
  const d = new Date(ms)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`
}

export function AgentTerminal({
  person,
  organizationId,
  getToken,
  onClose,
}: {
  person: Person
  organizationId: string
  getToken: GetToken
  onClose: () => void
}) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const client = async () => createClient(await getToken())

  const account = useQuery({
    queryKey: ['agentAccount', organizationId, person.userId],
    queryFn: async () =>
      fetchAgentAccount(await client(), { organizationId, userId: person.userId }),
    refetchInterval: 15_000,
    retry: false,
  })
  const agentAccountId = account.data?.id

  const activities = useQuery({
    queryKey: ['agentTerminal', agentAccountId],
    queryFn: async () => fetchAgentActivities(await client(), agentAccountId!, 120),
    enabled: Boolean(agentAccountId),
    refetchInterval: 8_000,
    retry: false,
  })

  // Oldest-first for a terminal; keep pinned to the newest line.
  const lines = [...(activities.data ?? [])].reverse()
  const lastId = lines.at(-1)?.id
  useEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [lastId])

  const presence = account.data?.presence
  const state = (presence?.state ?? 'OFFLINE').toUpperCase()
  const beatMs = presence?.lastHeartbeatAt ? parseApiTime(presence.lastHeartbeatAt) : NaN
  const beatAgo = Number.isNaN(beatMs)
    ? null
    : Math.max(0, Math.round((Date.now() - beatMs) / 1000))

  return (
    <div style={backdrop} onClick={onClose}>
      <div style={shell} onClick={(e) => e.stopPropagation()}>
        <div style={titleBar}>
          <span style={{ display: 'flex', gap: 6 }}>
            <span style={{ ...dot, background: '#ff5f57' }} />
            <span style={{ ...dot, background: '#febc2e' }} />
            <span style={{ ...dot, background: '#28c840' }} />
          </span>
          <span style={titleText}>
            {person.name.toLowerCase().replace(/\s+/g, '-')}@
            {account.data?.office?.name.toLowerCase().replace(/\s+/g, '-') ?? 'no-office'} —
            work log
          </span>
          <button onClick={onClose} style={closeBtn}>✕</button>
        </div>

        <div style={statusLine}>
          <span style={{ color: STATE_COLOR[state] ?? theme.textDim, fontWeight: 700 }}>
            ● {state.toLowerCase()}
          </span>
          {(presence?.label ?? presence?.summary) && (
            <span style={{ color: theme.textDim }}> · {presence?.label ?? presence?.summary}</span>
          )}
          {beatAgo != null && (
            <span style={{ color: theme.textDim }}>
              {' '}· heartbeat {beatAgo < 90 ? `${beatAgo}s` : `${Math.round(beatAgo / 60)}m`} ago
            </span>
          )}
        </div>

        <div ref={scrollRef} style={body}>
          {account.isLoading && <div style={{ color: theme.textDim }}>connecting…</div>}
          {!account.isLoading && !account.data && (
            <div style={{ color: theme.textDim }}>
              no agent account visible — org owners only.
            </div>
          )}
          {lines.length === 0 && account.data && !activities.isLoading && (
            <div style={{ color: theme.textDim }}>no activity reported yet.</div>
          )}
          {lines.map((a) => {
            const color = TYPE_COLOR[a.type.toLowerCase()] ?? '#c9d2e0'
            return (
              <div key={a.id} style={line}>
                <span style={{ color: '#4d5568' }}>[{clock(a.createdAt)}]</span>{' '}
                <span style={{ color, fontWeight: 600 }}>{a.type}</span>
                <span style={{ color: '#4d5568' }}> › </span>
                <span style={{ color: '#d5dae4' }} title={a.summary ?? undefined}>
                  {a.label ?? a.summary ?? ''}
                </span>
                {a.status && (
                  <span
                    style={{
                      color:
                        a.status === 'FAILED' || a.status === 'BLOCKED'
                          ? '#ff6b6b'
                          : a.status === 'DONE'
                            ? '#54d18c'
                            : '#e0a13a',
                    }}
                  >
                    {' '}[{a.status.toLowerCase()}]
                  </span>
                )}
              </div>
            )
          })}
          <div style={{ color: '#54d18c' }}>
            ▊<span style={{ color: '#4d5568' }}> live · refreshes every 8s</span>
          </div>
        </div>
      </div>
    </div>
  )
}

const backdrop: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  background: '#140d07c9',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
}
const shell: React.CSSProperties = {
  width: 'min(760px, 92vw)',
  height: 'min(480px, 78vh)',
  display: 'flex',
  flexDirection: 'column',
  background: '#0b0d12',
  border: `1px solid ${theme.border}`,
  borderRadius: 12,
  overflow: 'hidden',
  boxShadow: '0 24px 80px #000c',
}
const titleBar: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '9px 12px',
  background: '#14161d',
  borderBottom: `1px solid ${theme.border}`,
}
const dot: React.CSSProperties = { width: 10, height: 10, borderRadius: '50%' }
const titleText: React.CSSProperties = {
  flex: 1,
  textAlign: 'center',
  fontFamily: 'ui-monospace, monospace',
  fontSize: 12,
  color: theme.textDim,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}
const closeBtn: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: theme.textDim,
  cursor: 'pointer',
  fontSize: 13,
}
const statusLine: React.CSSProperties = {
  padding: '7px 14px',
  fontFamily: 'ui-monospace, monospace',
  fontSize: 11.5,
  borderBottom: `1px solid ${theme.border}`,
  background: '#0e1016',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}
const body: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: '10px 14px',
  fontFamily: 'ui-monospace, monospace',
  fontSize: 11.5,
  lineHeight: 1.65,
}
const line: React.CSSProperties = { overflowWrap: 'anywhere' }
